import { addMonths, format, parse, startOfMonth } from 'date-fns';
import React, { useEffect, useRef } from 'react';
import { useIsFetching, useQuery } from 'react-query';
import { InputElement } from 'components/ui/Form/Input';
import { Option, Select } from 'components/ui/Form/Select';
import { dateFormats } from 'utils/Dates';
import { FETCH_RUNPAYROLL_DATA } from 'components/RunPayroll/utils/queries';
import { Box } from '@razorpay/blade/components';
import api from 'api';
import { queryKeys } from './data';

const DEFAULT_LOOKBACK_MONTHS = 24;

function generateMonthRange(joiningDate?: string): Date[] {
  const today = startOfMonth(new Date());
  let start = joiningDate
    ? startOfMonth(parse(joiningDate, dateFormats.yearMonth, new Date()))
    : addMonths(today, -DEFAULT_LOOKBACK_MONTHS);

  if (start > today) start = addMonths(today, -DEFAULT_LOOKBACK_MONTHS);

  const months: Date[] = [];
  for (let cur = start; cur <= today; cur = addMonths(cur, 1)) {
    months.push(new Date(cur));
  }
  return months.reverse();
}

export const MonthSelector = ({
  label,
  value,
  onChange,
  selectedMonths,
  isDisabled,
  orgId,
  userId,
  joiningDate,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  selectedMonths: string[];
  isDisabled: boolean;
  orgId: string;
  userId: string;
  joiningDate?: string;
}) => {
  const dropdownRef = useRef<HTMLSelectElement>(null);
  const isFetchingData = useIsFetching(FETCH_RUNPAYROLL_DATA);

  const {
    data: payrollMonthsData,
    isLoading: isLoadingMonths,
    error: errorFetchingMonths,
  } = useQuery({
    queryKey: [queryKeys.GET_USER_PAYROLL_MONTHS, orgId, userId],
    queryFn: () =>
      api.arrears.queries.getUserPayrollMonthsByFilter({
        merchantId: orgId,
        tenantId: orgId,
        userCriteria: [
          {
            userId,
          },
        ],
        filters: {
          status: 'status_paid',
          getMonthsWithLopOnly: false,
        },
      }),
    enabled: !!orgId && !!userId,
  });

  const hasError =
    Boolean(errorFetchingMonths) ||
    payrollMonthsData?.payrollGetUserPayrollMonthsByFilter.success === false;

  const paidMonthsSet = new Set<string>(
    payrollMonthsData?.payrollGetUserPayrollMonthsByFilter?.data?.userPayrollMonths?.[0]
      ?.payrollMonths ?? [],
  );

  const monthList = generateMonthRange(joiningDate);

  const onSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    onChange(selectedValue);
  };

  useEffect(() => {
    if (!value) {
      if (dropdownRef.current) {
        dropdownRef.current.focus();
      }
    }
  }, []);

  const isDisabledState = !!isFetchingData || isDisabled || isLoadingMonths;

  return (
    <Box>
      <InputElement
        required
        label={label ? label : 'Select Payroll Month'}
        className={'payroll-month-selector-wrapper'}>
        <Select
          ref={dropdownRef}
          name="pm"
          onChange={onSelectionChange}
          value={value}
          disabled={isDisabledState}
          className="month-selector"
          aria-label="Select Payroll Month">
          {value === '' && <Option value="">Select Month</Option>}
          {monthList.map((month) => {
            const monthKey = format(month, dateFormats.yearMonth);
            const isPaid = paidMonthsSet.has(monthKey);
            return (
              <Option
                key={month.toDateString()}
                value={monthKey}
                data-date={month}
                disabled={selectedMonths.includes(monthKey)}>
                {isPaid
                  ? `${format(month, dateFormats.monthYear)} (payroll run)`
                  : format(month, dateFormats.monthYear)}
              </Option>
            );
          })}
        </Select>
      </InputElement>
    </Box>
  );
};
